import { useSelector } from "react-redux"
import { useNotification } from "./useNotification";
import { AlertColor, Box, IconButton, LinearProgress, Snackbar, Typography, useTheme } from "@mui/material";
import { INotification } from "../../interfaces/notification/INotification";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";

const SEVERITY_CONFIG: Record<AlertColor, { darkColor: string; lightColor: string; icon: React.ReactNode }> = {
    success: { darkColor: '#00FFA3', lightColor: '#00916A', icon: <CheckCircleOutlineIcon fontSize="small" /> },
    error:   { darkColor: '#FF4F4F', lightColor: '#D32F2F', icon: <ErrorOutlineIcon fontSize="small" />       },
    warning: { darkColor: '#FFD700', lightColor: '#C9A800', icon: <WarningAmberIcon fontSize="small" />       },
    info:    { darkColor: '#00D1FF', lightColor: '#0099BB', icon: <InfoOutlinedIcon fontSize="small" />        },
};

export const Notification = () => {
    const notification: INotification = useSelector((state: any) => state?.notification);
    const { clearNotification } = useNotification();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const config = SEVERITY_CONFIG[notification?.type] ?? SEVERITY_CONFIG.info;
    const accentColor = isDark ? config.darkColor : config.lightColor;
    const timeout = notification?.timeout ?? 5000;

    const cardBg       = isDark ? 'rgba(26, 26, 46, 0.97)'      : 'rgba(255, 255, 255, 0.98)';
    const cardBorder   = isDark ? 'rgba(255,255,255,0.07)'       : 'rgba(13,13,26,0.10)';
    const cardShadow   = isDark ? '0 8px 32px rgba(0,0,0,0.45)' : '0 8px 32px rgba(0,0,0,0.12)';
    const textColor    = isDark ? 'rgba(255,255,255,0.82)'       : 'rgba(13,13,26,0.82)';
    const closeColor   = isDark ? 'rgba(255,255,255,0.35)'       : 'rgba(13,13,26,0.35)';
    const closeHoverBg = isDark ? 'rgba(255,255,255,0.07)'       : 'rgba(13,13,26,0.07)';
    const closeHoverFg = isDark ? '#fff'                         : '#0D0D1A';
    const progressBg   = isDark ? 'rgba(255,255,255,0.05)'       : 'rgba(13,13,26,0.07)';

    return (
        <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={timeout}
            open={notification?.open}
            onClose={clearNotification}
        >
            <Box
                sx={{
                    position: 'relative',
                    minWidth: 300,
                    maxWidth: 420,
                    background: cardBg,
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${cardBorder}`,
                    borderLeft: `3px solid ${accentColor}`,
                    borderRadius: '8px',
                    boxShadow: cardShadow,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', p: '14px 16px', gap: 1.5 }}>
                    <Box sx={{ color: accentColor, display: 'flex', mt: '2px', flexShrink: 0 }}>
                        {config.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{ color: textColor, fontSize: '0.875rem', lineHeight: 1.45 }}
                        >
                            {notification?.message ?? "An unexpected error occurred."}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={clearNotification}
                        sx={{
                            color: closeColor,
                            mt: '-2px',
                            mr: '-4px',
                            flexShrink: 0,
                            '&:hover': { color: closeHoverFg, background: closeHoverBg },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                {notification?.open && (
                    <LinearProgress
                        variant="determinate"
                        value={100}
                        sx={{
                            height: 2,
                            backgroundColor: progressBg,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: accentColor,
                                transformOrigin: 'left',
                                animation: `notifShrink ${timeout}ms linear forwards`,
                                '@keyframes notifShrink': {
                                    from: { transform: 'translateX(0%)' },
                                    to:   { transform: 'translateX(-100%)' },
                                },
                            },
                        }}
                    />
                )}
            </Box>
        </Snackbar>
    );
}